// The online tier's HTTP API as slice 9a fixed it (docs/design/online-tier.md, decision 8), for
// the page, the Worker's sync loop and later the native app. The page calls it at its own origin,
// where Caddy passes /api to the server, and learns from the session answer whether there is a
// server at all. A browser session is the `interplae_session` cookie, which a change also needs
// `X-Interplae: 1` beside; a native client sends a bearer instead. Every request names this
// device in `X-Interplae-Device`.

import type { BatchPreview, Changes, CloseoutTerms, Cursor, DocumentInfo, EngineError, HttpRequest, HttpResponse, ImportInto, ImportTerms, MemoryPick, ProjectRole, PromoteBatch, Request, ResponseOf, SegmentSize, TermChoices, Through } from '../engine/protocol.ts'

export type { MemoryPick }
import { sha256 } from '../core/pair.ts'
import type { ServerAdapted } from '../core/pdf.ts'

/// A signed-in person: their account, the role the organisation gave them (ADR 0027, decision
/// 6), and their session's device.
export interface Member {
  account: string
  email: string
  name: string
  role: 'admin' | 'manager' | 'user'
  device: string
}

/// One person on a project with their project role (ADR 0027, decision 7).
export interface ProjectPerson {
  account: string
  role: ProjectRole
}

/// A project's people after a change, and whether it returned the Lease the person held there.
export interface Assigned {
  people: ProjectPerson[]
  returned: boolean
}

/// A project by its id and name.
export interface ProjectName {
  id: string
  name: string
}

/// A member as People lists them: whether an Admin disabled them, how many sessions they have
/// live, and the projects whose Lease they hold, which disabling them returns.
export interface Person {
  account: string
  email: string
  name: string
  role: Member['role']
  disabled: boolean
  sessions: number
  holds: ProjectName[]
}

/// An invite nobody has taken up yet: the People section's Invited tab.
export interface OpenInvite {
  id: string
  email: string
  role: Member['role']
  expires: string
}

/// What an Admin changes about a member: their role, or whether they may sign in.
export type MemberChange = { role: Member['role'] } | { disabled: boolean }

/// The member after a change, the sessions it ended and the Leases it returned.
export interface MemberChanged {
  member: Person
  sessionsEnded: number
  returned: ProjectName[]
}

/// A server project's Lease: free, or held by one account on one device since a time.
export type LeaseState =
  | { kind: 'free'; epoch: number }
  | { kind: 'held'; epoch: number; holder: string; holderName: string; device: string; since: string }

/// One heading range of a project as the organisation's Home lists it (ADR 0027 decisions 9 and
/// 13): its document, heading, assignee and who holds its Lease.
export interface ServerRange {
  id: number
  document: number
  heading: string | null
  assignee: string
  assigneeName: string
  lease: LeaseState
  /// Who turned the range in for review and when, until its next take (brief 29).
  turnedIn?: TurnedIn
}

/// A range turned in for review: the account, its name and when.
export interface TurnedIn {
  by: string
  byName: string
  at: string
}

/// One project as the organisation's Home lists it, counted from its file without opening it.
export interface ServerProject {
  id: string
  name: string
  lease: LeaseState
  /// Who last took the Lease and when.
  lastOpened: [string, string] | null
  /// The sequence of the last write the server received.
  applied: number
  total: number
  untouched: number
  /// The unreviewed Segments by where their target came from: memory, machine, previous, human.
  unreviewed: Record<string, number>
  confirmed: number
  approved: number
  /// The digest the project's shared file is named by, which a device that keeps that file asks
  /// a checkpoint without.
  shared: string | null
  /// The signed-in person's role on the project, null for a Manager or an Admin not on it.
  role: ProjectRole | null
  /// The project's people with their project roles.
  people: ProjectPerson[]
  /// Each Source Document in position order with its own counts (ADR 0027 decision 8).
  documents: DocumentInfo[]
  /// Every range of every document.
  ranges: ServerRange[]
  /// When a restore replaced the project, who restored it, and the project that replaced it
  /// (ADR 0029 decision 3): an Archived Project, read-only.
  archived?: Archived
  /// The last restore that wrote the project from a bundle.
  restored?: RestoreRecord
}

/// The project a restore replaced an archived one with.
export interface Replacement {
  id: string
  name: string
}

/// An Archived Project's record: when, who restored the bundle, and what replaced it.
export interface Archived {
  at: string
  by: string
  byName: string
  replacedBy: Replacement
}

/// One restore in a project's history (ADR 0029 decision 4): who, when, the bundle's date, the
/// server it came from, null for a device's, and the nightly copy it came from by its generation,
/// null for an uploaded bundle (decision 9).
export interface RestoreRecord {
  account: string
  name: string
  at: string
  bundleAt: string
  origin: string | null
  night: string | null
}

/// A project in a nightly copy kept on the server (ADR 0029 decision 9): deleted when the server
/// no longer has it, archived when it was archived that night.
export interface NightProject {
  id: string
  name: string
  deleted: boolean
  archived: boolean
}

/// What a bundle is, as a restore's preview says (ADR 0029 decision 6).
export type BundleKind =
  | { kind: 'this' }
  | { kind: 'organisation'; project: string; name: string; here: boolean }
  | { kind: 'elsewhere'; host: string }
  | { kind: 'device' }

/// What restoring a bundle would do (ADR 0029 decisions 1, 2, 5 and 6), before the commit.
export interface RestorePreview {
  upload: string
  kind: BundleKind
  name: string
  date: string
  sequence: number | null
  changes: number | null
  replaces: ProjectName | null
  holders: { account: string; name: string; since: string; syncedAt: string | null; ranges: number[] }[]
  people:
    | { from: 'original'; project: string; name: string; people: ProjectPerson[]; editor: boolean }
    | { from: 'night'; night: string; people: ProjectPerson[]; editor: boolean; editorAdded: boolean }
    | { from: 'none'; candidates: { account: string; name: string }[] }
  becomes: string
  restoredBefore: ProjectName[]
  pending: string[]
  unassigned: { document: number; heading: string | null; was: string | null }[]
}

/// A checkpoint as the server answered it, and the digest its shared file is named by: when
/// that is the digest the request named, the zip carries the project file alone. `sha256` is
/// the SHA-256 of the bytes the server holds under that digest.
export interface Checkpoint {
  bytes: ArrayBuffer
  shared: string | null
  sha256: string
}

/// What a take hands the device: the capability every push carries, the role the taker holds
/// on the project, which this device writes as, and the ranges it holds (ADR 0027 decision 9).
export interface Grant {
  project: string
  capability: string
  epoch: number
  role: ProjectRole
  ranges?: number[]
}

export interface NewProject {
  name: string
  sourceName: string
  sourceLanguage: string
  targetLanguage: string
  prefillAtLeast: number | null
  segmentSize: SegmentSize
  /// The glossary lists the project reads with the choices over their terms, and the
  /// organisation's memory it reads (ADR 0027 decision 12); absent, the project reads no vault.
  glossary?: { lists: string[] } & TermChoices
  memory?: MemoryPick | null
}

/// One of the organisation's glossary vaults at its current revision (docs/design/online-tier.md,
/// decision 5): one list. The organisation's memory has no vaults (owner-log issue 04).
export type Vault = { id: string; name: string; revision: string; digest: string; kind: 'glossary'; list: string }

/// Which glossary vaults a project reads, in read order, and which of the organisation's memory
/// sources. Its terms go in batches, each naming a glossary vault; its approved Segments go into
/// the organisation's memory at close-out, whatever it reads. `exclude` and `prohibited` are the
/// manager's choices over every term of the chosen glossary vaults, written in the binding (ADR
/// 0027 decision 12); absent, every term keeps its state.
export interface VaultSelection extends TermChoices {
  vaults: string[]
  /// The organisation's memory sources by All, by collections or by projects (ADR 0027 decision
  /// 12), resolved when the snapshot is made.
  memory?: MemoryPick | null
}


/// The requests of the organisation's two screens on its own file, a Manager's or an Admin's.
export type OrganisationScreen = 'library' | 'memory'

/// The vaults a project reads now, as the snapshot its shared file was copied from. A standalone
/// project has no snapshot.
export interface ProjectVaults {
  snapshot: { digest: string; vaults: Vault[]; memory?: { pick: MemoryPick; keys: string[]; digest: string } } | null
  /// The memory holds newer pairs than the snapshot copied, which Update brings.
  memoryNewer: boolean
  /// Where the project's close-out files new sources, as its last close-out chose; null before
  /// its first.
  closeoutFiling: SavedFiling | null
  /// The names of the collections and projects the memory pick names, by id.
  names?: Record<string, string>
}

/// A collection of the organisation's memory by id and name.
export interface CollectionRef {
  id: string
  name: string
}

/// The collection a project's last close-out filed new sources in, null for none or one removed
/// since, and when that close-out chose it.
export interface SavedFiling {
  collection: CollectionRef | null
  chosenAt: string
}

/// Where a close-out files the sources it makes: an existing collection, a new one by this name,
/// or none when null.
export type CloseoutCollection = { existing: string } | { create: string } | null

/// One document as the close-out sheet counts it before a close-out, and the source its pairs
/// would go under: new, or one a close-out made before, with its collections.
export interface PlannedDocument {
  document: number
  label: string
  approved: number
  confirmedNotApproved: number
  unreviewed: number
  untouched: number
  source: string
  sourceNew: boolean
  firstClosedOut: string | null
  collections: CollectionRef[]
  lastCloseout: string | null
}

/// What the close-out sheet reads before a server close-out (owner-log issue 04, D3).
export interface CloseoutPlan {
  documents: PlannedDocument[]
  saved: SavedFiling | null
  collections: CollectionRef[]
  /// What the project itself reads of the organisation's memory, a separate setting.
  reads: MemoryPick | null
  /// The project's latest close-out, so a sheet that opens on a stopped one offers Resume.
  last: CloseoutState | null
}

/// A collection by id and name, and whether this close-out made it.
export interface Filed extends CollectionRef {
  created: boolean
}

/// One document's part of a close-out, every count the server's: `approved` is `wentIn` +
/// `removed` + `missing`, and `contributions` (the Segments that went in this time) is
/// `pairsNew` + `pairsHeld` + `pairsRepeated`.
export interface DocumentPromoted {
  document: number
  label: string
  source: string
  sourceNew: boolean
  firstClosedOut: string
  filed: Filed | null
  collections: CollectionRef[]
  approved: number
  wentIn: number
  contributions: number
  earlier: number
  removed: number
  missing: number
  pairsNew: number
  pairsHeld: number
  pairsRepeated: number
}

/// A project that gets a close-out's pairs at its next Update, and why.
export interface CloseoutReader {
  project: string
  name: string
  reads: 'all' | 'collections' | 'projects'
  collections?: string[]
}

/// What a close-out did, in total and per document, where it filed new sources and which
/// projects get its pairs at their next Update. `segments.pairsNew` counts pairs the memory did
/// not hold, each once, as the Memories screen counts a pair.
export interface Closeout {
  job: string
  operation: string
  cursor: Cursor
  approved: number
  confirmedNotApproved: number
  unreviewed: number
  untouched: number
  /// Each document's counts, in position order.
  documents: { id: number; label: string; approved: number; confirmedNotApproved: number; unreviewed: number; untouched: number }[]
  segments: {
    pairsNew: number
    contributions: number
    wentIn: number
    earlier: number
    removed: number
    missing: number
    pairsHeld: number
    pairsRepeated: number
    entriesNew: number
    entriesPresent: number
    documents: DocumentPromoted[]
  }
  filing: Filed | null
  readers: CloseoutReader[]
}

/// A close-out as the server knows it: done with its answer, running, or stopped with the
/// documents that went in before it stopped.
export type CloseoutState =
  | { state: 'done'; operation: string; result: Closeout }
  | { state: 'running'; operation: string }
  | { state: 'stopped'; operation: string; acceptedAt: string; filing: Filed | null; documents: { document: number; label: string; approved: number; done: DocumentPromoted | null }[] }

/// What a batch of a project's terms wrote into a glossary vault (ADR 0025 decision 13), the
/// vault as it left it, and how many of the project's terms are not yet addressed.
export interface TermsBatch {
  job: string
  cursor: Cursor
  vault: string
  list: { id: string; name: string }
  promoted: number
  revised: number
  present: number
  replaced: number
  alternatives: number
  revision: Vault
  notAddressed: number
}

/// Why the server refused: its own kinds, a held Lease with its holder, or the engine's error.
export type ServerError =
  | { kind: 'sign-in'; message: string; disabled?: true }
  | { kind: 'forbidden' | 'not-found' | 'parse' | 'too-large' | 'conflict' | 'storage'; message: string }
  | { kind: 'ingest-failed' | 'ingest-unconfigured' | 'not-a-pdf' | 'adapt' | 'place'; message: string }
  /// The hand-off's one refusal and a suspended organisation's answer, both 403
  /// (docs/design/sign-in-handoff.md, decisions 5 and 8).
  | { kind: 'handoff-refused' | 'suspended'; message: string }
  /// A sign-in without a token Cloudflare's Turnstile passed, 400, or with Cloudflare out of
  /// reach, 502 (brief 24).
  | { kind: 'challenge-failed' | 'challenge-unavailable'; message: string }
  | { kind: 'lease-held'; message: string; holder: string; holders?: string[]; since: string }
  /// A write to an Archived Project, 409, naming its replacement; a server short of disk for a
  /// restore, 507; another Download all running, 503 (ADR 0029).
  | { kind: 'archived'; message: string; replacedBy: Replacement }
  | { kind: 'no-room'; message: string }
  | EngineError

export class ServerFailure extends Error {
  constructor(readonly status: number, readonly error: ServerError) {
    super(error.message)
    this.name = 'ServerFailure'
  }
}

/// What the session answer makes of the page: no server behind it (today's Home), a server and
/// nobody signed in, a session a manager ended by disabling its account, a member, or a server
/// that does not answer.
export type Mode = { kind: 'none' } | { kind: 'signed-out' } | { kind: 'disabled' } | { kind: 'member'; member: Member } | { kind: 'down' }

/// A status with no body the server wrote, such as a route a later slice adds.
const UNANSWERED: Record<number, ServerError['kind']> = { 401: 'sign-in', 403: 'forbidden', 404: 'not-found', 409: 'conflict', 413: 'too-large' }

/// Told the bytes a download has received so far and the total its response names, if any.
export type Progress = (received: number, total: number | null) => void

/// A response's body read in chunks, telling `progress` after each (brief 21). The total is the
/// response's Content-Length, dropped once more arrives than it named, as a compressed body would.
async function bodyOf(response: Response, progress: Progress | null): Promise<ArrayBuffer> {
  if (!progress || !response.body) return response.arrayBuffer()
  const named = Number(response.headers.get('content-length'))
  let total: number | null = Number.isFinite(named) && named > 0 ? named : null
  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  progress(0, total)
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    received += value.byteLength
    if (total !== null && received > total) total = null
    progress(received, total)
  }
  const bytes = new Uint8Array(received)
  let at = 0
  for (const chunk of chunks) {
    bytes.set(chunk, at)
    at += chunk.byteLength
  }
  return bytes.buffer
}

export class Api {
  /// `origin` is the server's, empty for the page's own; `device` names this device to the server.
  constructor(readonly origin: string, readonly device: string, private readonly fetcher: typeof fetch = (input, init) => fetch(input, init), private readonly bearer: string | null = null) {}

  private headers(method: string, extra: [string, string][] = []): Headers {
    const headers = new Headers(extra)
    headers.set('x-interplae-device', this.device)
    if (method !== 'GET') headers.set('x-interplae', '1')
    if (this.bearer) headers.set('authorization', `Bearer ${this.bearer}`)
    return headers
  }

  private async request(method: string, path: string, body?: unknown, extra: { type?: string; signal?: AbortSignal | undefined; headers?: [string, string][] } = {}): Promise<Response> {
    const json = body !== undefined && !(body instanceof ArrayBuffer)
    const type = json ? 'application/json' : extra.type
    const headers = this.headers(method, [...(type ? [['content-type', type] as [string, string]] : []), ...(extra.headers ?? [])])
    const response = await this.fetcher(`${this.origin}${path}`, { method, headers, body: body === undefined ? null : json ? JSON.stringify(body) : (body as ArrayBuffer), credentials: 'include', signal: extra.signal ?? null })
    if (response.ok) return response
    const text = await response.text()
    let error: ServerError
    try {
      error = JSON.parse(text) as ServerError
    } catch {
      error = { kind: UNANSWERED[response.status] ?? 'storage', message: response.status === 404 ? 'not available yet' : text || `the server answered ${response.status}` } as ServerError
    }
    throw new ServerFailure(response.status, error)
  }

  private async json<T>(method: string, path: string, body?: unknown): Promise<T> {
    return (await (await this.request(method, path, body)).json()) as T
  }

  /// Which page this is, from `GET /api/session`: 404 with no server, 401 to sign in, 200 the
  /// member, and a gateway's 502 or no answer with the server down.
  async mode(): Promise<Mode> {
    try {
      return { kind: 'member', member: await this.json<Member>('GET', '/api/session') }
    } catch (e) {
      if (e instanceof ServerFailure && e.status === 404) return { kind: 'none' }
      if (e instanceof ServerFailure && e.status === 401) return e.error.kind === 'sign-in' && e.error.disabled ? { kind: 'disabled' } : { kind: 'signed-out' }
      if (e instanceof TypeError || (e instanceof ServerFailure && e.status >= 502 && e.status <= 504)) return { kind: 'down' }
      throw e
    }
  }

  /// The organisation's name and the contract the server speaks, which anyone may read, the
  /// organisation's key, which the hand-off names to the site, and the site's origin, null when
  /// the organisation has no control service (brief 18), and the Turnstile site key the sign-in
  /// renders its widget with, null when the server sets none (brief 24).
  about(): Promise<{ name: string; api: number; key?: string; site?: string | null; turnstile?: string | null }> {
    return this.json('GET', '/api/about')
  }

  /// Redeems a hand-off code from the site with the verifier this tab kept, for a cookie
  /// session on this organisation (docs/design/sign-in-handoff.md, decision 3).
  handoff(code: string, verifier: string): Promise<Member> {
    return this.json('POST', '/api/auth/handoff', { code, verifier })
  }

  /// Mails a sign-in link to a member or an invited address; anyone else gets the same answer.
  /// The form's Turnstile token goes with it when the page has the widget.
  async requestLink(email: string, turnstile: string | null = null): Promise<void> {
    await this.request('POST', '/api/auth/link', turnstile ? { email, turnstile } : { email })
  }

  /// Signs this browser in with a mailed link's token, which works once.
  consumeLink(token: string): Promise<Member> {
    return this.json('POST', '/api/auth/link/consume', { token, device: this.device })
  }

  async signOut(): Promise<void> {
    await this.request('POST', '/api/auth/logout')
  }

  /// Approves a device's sign-in request by its code, as the member this browser is signed in
  /// as (docs/design/native-app.md, decision 5).
  async approveDevice(code: string): Promise<void> {
    await this.request('POST', '/api/auth/device/approve', { request: code })
  }

  projects(): Promise<ServerProject[]> {
    return this.json('GET', '/api/projects')
  }

  /// A Manager's new project with its people: the source goes up as a blob by its SHA-256, then
  /// the create names it.
  /// A new project from its first file and any further `documents`, each uploaded first and named
  /// beside the request as the engine reads it (ADR 0027 decision 12).
  async createProject(bytes: ArrayBuffer, request: NewProject, people: ProjectPerson[], documents: { name: string; bytes: ArrayBuffer; structure?: ArrayBuffer | null }[] = [], structure: ArrayBuffer | null = null): Promise<{ id: string }> {
    const upload = async (field: string, bytes: ArrayBuffer) => {
      const digest = await sha256(bytes)
      await this.request('PUT', `/api/blobs/${digest}`, bytes)
      return { field, sha256: digest }
    }
    const files = [await upload('bytes', bytes)]
    if (structure) files.push(await upload('structure', structure))
    for (const [n, d] of documents.entries()) {
      files.push(await upload(`documents.${n}.bytes`, d.bytes))
      if (d.structure) files.push(await upload(`documents.${n}.structure`, d.structure))
    }
    const body = documents.length ? { ...request, documents: documents.map(d => ({ name: d.name })) } : request
    return this.json('POST', '/api/projects', { request: body, files, people })
  }

  /// A Manager puts a person on a project with a role, changes it, or takes them off it with null.
  assign(id: string, account: string, role: ProjectRole | null): Promise<Assigned> {
    return this.json('POST', `/api/projects/${id}/people`, { account, role })
  }

  /// A bundle to restore, uploaded whole to the organisation's server (ADR 0029): its handle.
  uploadRestore(bytes: ArrayBuffer): Promise<{ upload: string; size: number }> {
    return this.json('POST', '/api/restores', bytes)
  }

  /// What restoring the upload would do, replacing `replace` or from Home with null.
  restorePreview(upload: string, replace: string | null): Promise<RestorePreview> {
    return this.json('GET', `/api/restores/${upload}${replace ? `?replace=${encodeURIComponent(replace)}` : ''}`)
  }

  /// The restore itself: the project it made and the one it archived.
  restoreCommit(upload: string, replace: string | null, people: ProjectPerson[]): Promise<{ id: string; name: string; replaced: string | null }> {
    return this.json('POST', `/api/restores/${upload}`, { ...(replace ? { replace } : {}), ...(people.length ? { people } : {}) })
  }

  /// The nightly copies kept on the server an Admin may restore a project from, newest first.
  nights(): Promise<string[]> {
    return this.json('GET', '/api/nights')
  }

  /// The projects one kept night holds.
  nightProjects(night: string): Promise<NightProject[]> {
    return this.json('GET', `/api/nights/${encodeURIComponent(night)}/projects`)
  }

  /// What restoring a project from a kept night would do, as a bundle's preview says it.
  nightPreview(night: string, project: string): Promise<RestorePreview> {
    return this.json('GET', `/api/nights/${encodeURIComponent(night)}/projects/${encodeURIComponent(project)}`)
  }

  /// The restore from a kept night: a new project, with that night's people.
  nightCommit(night: string, project: string): Promise<{ id: string; name: string; replaced: string | null }> {
    return this.json('POST', `/api/nights/${encodeURIComponent(night)}/projects/${encodeURIComponent(project)}`, {})
  }

  /// The upload of a restore the person did not go on with, removed.
  async discardRestore(upload: string): Promise<void> {
    await this.request('DELETE', `/api/restores/${upload}`)
  }

  /// Download all projects, an Admin's (ADR 0029 decision 8): one zip of every project's bundle.
  async downloadAll(): Promise<{ name: string; bytes: ArrayBuffer }> {
    const response = await this.request('GET', '/api/download/projects')
    const named = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'projects.zip'
    return { name: named, bytes: await response.arrayBuffer() }
  }

  /// A project's bundle as a person saves it: the server's copy with the shared file inside, and
  /// the loose Source Documents a take's checkpoint leaves out.
  async bundle(id: string): Promise<ArrayBuffer> {
    return (await this.request('GET', `/api/projects/${id}/bundle`)).arrayBuffer()
  }

  async deleteProject(id: string): Promise<void> {
    await this.request('DELETE', `/api/projects/${id}`)
  }

  /// The Lease for this device: when it is free, or held by the same account on another device.
  take(id: string): Promise<Grant> {
    return this.json('POST', `/api/projects/${id}/take`, { device: this.device })
  }

  lease(id: string): Promise<LeaseState> {
    return this.json('GET', `/api/projects/${id}/lease`)
  }

  /// The checkpoint a take installs or a view reads: the server's copy at its last acknowledged
  /// write. Naming the digest of its shared file leaves that file out of the zip, for a device
  /// that holds it or fetches it by digest, and naming `sources`, the SHA-256 of each file of the
  /// project's revisions the device holds, leaves those out of the project file (build brief 51).
  async checkpoint(id: string, shared: string | null = null, progress: Progress | null = null, sources: string[] = []): Promise<Checkpoint> {
    const headers: [string, string][] = []
    if (shared) headers.push(['x-interplae-shared', shared])
    if (sources.length > 0) headers.push(['x-interplae-sources', sources.join(',')])
    const response = await this.request('GET', `/api/projects/${id}/checkpoint`, undefined, { headers })
    const sha256 = response.headers.get('x-interplae-shared-sha256')
    if (!sha256) throw new Error('the server named no SHA-256 for the checkpoint\'s shared file')
    return { bytes: await bodyOf(response, progress), shared: response.headers.get('x-interplae-shared'), sha256 }
  }

  /// A shared file by the digest it is named by and `named`, the SHA-256 of the bytes the
  /// checkpoint named. That address never names other bytes, so the browser keeps its answer;
  /// a server that rebuilt the digest from other bytes since refuses it as `shared-changed`.
  async shared(digest: string, named: string, progress: Progress | null = null): Promise<ArrayBuffer> {
    return bodyOf(await this.request('GET', `/api/shared/${digest}?sha256=${named}`), progress)
  }

  /// Frees this device's ranges once the server holds every write of each, through `through`,
  /// and with `turnIn` marks them turned in for review.
  handBack(id: string, through: Through, turnIn = false): Promise<LeaseState> {
    return this.json('POST', `/api/projects/${id}/release`, { how: 'hand-back', through, ...(turnIn ? { turnIn } : {}) })
  }

  /// A Manager, an Admin or the project's Editor frees ranges at their last acknowledged state:
  /// one range, every range one person holds, or with neither every held range.
  takeBack(id: string, which: { range?: number; account?: string } = {}): Promise<LeaseState> {
    return this.json('POST', `/api/projects/${id}/take-back`, which)
  }

  /// The changes feed (docs/design/ranges.md decision 5): what the server's copy wrote after
  /// `since`, as the engine installs it.
  changes(id: string, since: number): Promise<Changes> {
    return this.json('GET', `/api/projects/${id}/changes?since=${since}`)
  }

  /// A request of the server's copy, in `document` when one is named: an Editor's review write
  /// outside the ranges this copy holds, or a range's assignment (ranges.md decision 6).
  /// A request's files, a document's source and its structure, or a Page Reference's PDF and
  /// placed Document, go up as blobs first, each under its field's name.
  async ask<T>(id: string, request: Request, document: number | null = null): Promise<T> {
    const files: { field: string; sha256: string }[] = []
    const named: Record<string, unknown> = {}
    for (const [field, value] of Object.entries(request)) {
      if (!(value instanceof ArrayBuffer)) {
        if (!(field === 'structure' && value === null)) named[field] = value
        continue
      }
      const digest = await sha256(value)
      await this.request('PUT', `/api/blobs/${digest}`, value)
      files.push({ field, sha256: digest })
    }
    const body = (files.length ? named : request) as Request
    return this.json('POST', `/api/workspaces/${id}/requests`, { request: document === null ? body : { ...body, document }, files })
  }

  /// The organisation's vaults at their current revisions, by name.
  vaults(): Promise<Vault[]> {
    return this.json('GET', '/api/vaults')
  }

  /// A request of the organisation's Glossaries or Memories screen on its own file. A request's
  /// bytes go up as a blob first; a file comes back as its name and bytes.
  async organisation<R extends Request>(screen: OrganisationScreen, request: R): Promise<ResponseOf<R>> {
    const { bytes, ...rest } = request as R & { bytes?: ArrayBuffer }
    const files: { field: string; sha256: string }[] = []
    if (bytes) {
      const digest = await sha256(bytes)
      await this.request('PUT', `/api/blobs/${digest}`, bytes)
      files.push({ field: 'bytes', sha256: digest })
    }
    const response = await this.request('POST', `/api/${screen}/requests`, { request: rest, files })
    if ((response.headers.get('content-type') ?? '').startsWith('application/json')) return (await response.json()) as ResponseOf<R>
    const name = /filename="([^"]*)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'export'
    return { name, bytes: await response.arrayBuffer() } as ResponseOf<R>
  }

  /// A PDF read on the organisation's server (program row 17): it goes up as the body and the
  /// ingestion service's digest, Document and report come back. `signal` aborts the upload.
  async adapt(pdf: ArrayBuffer, signal?: AbortSignal): Promise<ServerAdapted> {
    return (await (await this.request('POST', '/api/ingest/adapt', pdf, { type: 'application/pdf', signal })).json()) as ServerAdapted
  }

  /// Word's PDF of a DOCX project placed over the server's copy of its Document: the placed
  /// Document JSON, which the holder's engine attaches with the PDF.
  /// Word's PDF placed over `document` of the project, the one the server's copy has open when
  /// absent.
  async place(id: string, pdf: ArrayBuffer, document?: number): Promise<ArrayBuffer> {
    const query = document === undefined ? '' : `?document=${document}`
    return (await this.request('POST', `/api/projects/${id}/place${query}`, pdf, { type: 'application/pdf' })).arrayBuffer()
  }

  /// The vaults a project reads now.
  projectVaults(id: string): Promise<ProjectVaults> {
    return this.json('GET', `/api/projects/${id}/vaults`)
  }

  /// Change vaults' term table: the terms of the chosen glossary vaults as a binding now would
  /// read them, each at the project's state or its default. A manager's read.
  vaultTerms(id: string, vaults: string[]): Promise<ImportTerms> {
    return this.json('GET', `/api/projects/${id}/vault-terms?vaults=${encodeURIComponent(vaults.join(','))}`)
  }

  /// A manager's new set of vaults for the project, imported again while the Lease is free.
  changeVaults(id: string, selection: VaultSelection): Promise<{ cursor: Cursor }> {
    return this.json('POST', `/api/projects/${id}/vaults`, selection)
  }

  /// What the close-out sheet reads before a close-out. A manager's read.
  closeoutPlan(id: string): Promise<CloseoutPlan> {
    return this.json('GET', `/api/projects/${id}/closeout`)
  }

  /// A manager's close-out of the project into the organisation's memory under `operation`, its
  /// new sources filed as `collection` says. The same operation again resumes the close-out it
  /// started, with the collection it was accepted with.
  closeout(id: string, operation: string, collection: CloseoutCollection): Promise<Closeout> {
    return this.json('POST', `/api/projects/${id}/closeout`, { operation, collection })
  }

  /// A close-out by its operation, as the server knows it now.
  closeoutState(id: string, operation: string): Promise<CloseoutState> {
    return this.json('GET', `/api/projects/${id}/closeout/${encodeURIComponent(operation)}`)
  }

  // A project's terms at close-out, read from the server's copy and written in batches.

  closeoutTerms(id: string): Promise<CloseoutTerms> {
    return this.json('POST', `/api/workspaces/${id}/requests`, { request: { type: 'glossary.closeoutTerms' }, files: [] })
  }

  batchPreview(id: string, terms: string[], destination: ImportInto): Promise<BatchPreview[]> {
    return this.json('POST', `/api/workspaces/${id}/requests`, { request: { type: 'glossary.batchPreview', terms, destination }, files: [] })
  }

  /// A manager's batch into a glossary vault, as a job the operation names, so a resend after a
  /// lost answer gets the same one.
  promoteTerms(id: string, batch: PromoteBatch, operation: string): Promise<TermsBatch> {
    return this.json('POST', `/api/projects/${id}/terms`, { operation, ...batch })
  }

  leaveTerms(id: string, terms: string[]): Promise<{ left: number; notAddressed: number }> {
    return this.json('POST', `/api/workspaces/${id}/requests`, { request: { type: 'glossary.leave', terms }, files: [] })
  }

  async invite(email: string, role: Member['role']): Promise<string> {
    return (await this.json<{ invite: string }>('POST', '/api/invites', { email, role })).invite
  }

  /// The invites nobody has taken up, a Manager's or an Admin's to read.
  invites(): Promise<OpenInvite[]> {
    return this.json('GET', '/api/invites')
  }

  members(): Promise<Person[]> {
    return this.json('GET', '/api/members')
  }

  /// An Admin's change to a member: a role the next request reads, or disabling, which ends
  /// their sessions and returns their Leases, or enabling again.
  changeMember(account: string, change: MemberChange): Promise<MemberChanged> {
    return this.json('POST', `/api/members/${account}`, change)
  }

  /// One request of the engine's sync loop, with this session's headers. Never throws: a server
  /// that does not answer is a failure the engine records. `signal` aborts it, as a stopped sync
  /// loop does past its grace period.
  async send(request: HttpRequest, signal?: AbortSignal): Promise<HttpResponse> {
    const controller = new AbortController()
    let timedOut = false
    const timer = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, request.timeout_ms)
    const abort = () => controller.abort()
    signal?.addEventListener('abort', abort, { once: true })
    try {
      const response = await this.fetcher(request.url, { method: request.method, headers: this.headers(request.method, request.headers), body: request.body, credentials: 'include', signal: controller.signal })
      return { status: response.status, headers: [...response.headers.entries()], body: await response.text(), failure: null }
    } catch (e) {
      if (timedOut) return { status: 0, headers: [], body: '', failure: { kind: 'timeout' } }
      return { status: 0, headers: [], body: '', failure: { kind: 'unreachable', message: e instanceof Error ? e.message : String(e) } }
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', abort)
    }
  }
}
